import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "../../hooks/use-toast";
import { fmtINR } from "../../lib/format";
import { Save, Trash2, Plus, X, Calculator } from "lucide-react";

// Default rows used when a new FY is started
const DEFAULT_INCOME = [
  ["Speculative Income"],
  ["STCG"],
  ["LTCG"],
  ["ETF"],
  ["Dividend"],
  ["MF"],
];
const DEFAULT_EXPENSES = [
  ["Brokerage - Z"],
  ["Clearing Charges - Z"],
  ["Central GST - Z"],
  ["Securities Transaction Tax - Z"],
  ["Stamp Duty - Z"],
  ["IPFT"],
  ["Exchange Transaction Charges - Z"],
  ["State GST - Z"],
  ["Integrated GST - Z"],
  ["SEBI Turnover Fees - Z"],
  ["Other Credits & Debits"],
];
const DEFAULT_TAXES = [
  ["Income Tax"],
  ["Cess on tax"],
];

const newId = () => `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
const toRows = (arr) => arr.map(([name]) => ({ id: newId(), name, amount: 0 }));

function currentFY() {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() >= 3 ? y : y - 1;
  return `FY ${startYear}-${startYear + 1}`;
}

export default function ProfitLossForm() {
  const { toast } = useToast();
  const [statements, setStatements] = useState([]);
  const [fy, setFy] = useState(currentFY());
  const [income, setIncome] = useState(() => toRows(DEFAULT_INCOME));
  const [expenses, setExpenses] = useState(() => toRows(DEFAULT_EXPENSES));
  const [taxes, setTaxes] = useState(() => toRows(DEFAULT_TAXES));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadList = async () => {
    try {
      const rows = await api.listPnl();
      setStatements(rows);
    } catch (e) {}
  };
  useEffect(() => { loadList(); }, []);

  useEffect(() => {
    const existing = statements.find((s) => s.fiscalYear === fy);
    if (existing) {
      setIncome(Array.isArray(existing.income) ? existing.income : toRows(DEFAULT_INCOME));
      setExpenses(Array.isArray(existing.expenses) ? existing.expenses : toRows(DEFAULT_EXPENSES));
      setTaxes(Array.isArray(existing.taxes) ? existing.taxes : toRows(DEFAULT_TAXES));
      setNotes(existing.notes || "");
    } else {
      setIncome(toRows(DEFAULT_INCOME));
      setExpenses(toRows(DEFAULT_EXPENSES));
      setTaxes(toRows(DEFAULT_TAXES));
      setNotes("");
    }
  }, [fy, statements]);

  const totals = useMemo(() => {
    const sum = (rs) => rs.reduce((a, r) => a + (parseFloat(r.amount) || 0), 0);
    const gross = sum(income);
    const totalExp = sum(expenses);
    const totalTax = sum(taxes);
    const ebita = gross - totalExp;
    const pat = ebita - totalTax;
    return { gross, totalExp, totalTax, ebita, pat };
  }, [income, expenses, taxes]);

  const updateRow = (setter, id, field, v) =>
    setter((rows) =>
      rows.map((r) =>
        r.id === id
          ? { ...r, [field]: field === "amount" ? (v === "" ? 0 : parseFloat(v) || 0) : v }
          : r
      )
    );
  const addRow = (setter, name = "") =>
    setter((rows) => [...rows, { id: newId(), name, amount: 0 }]);
  const removeRow = (setter, id) => setter((rows) => rows.filter((r) => r.id !== id));

  const save = async () => {
    if (!fy.trim()) {
      toast({ title: "Fiscal year required", description: "e.g. FY 2025-2026" });
      return;
    }
    setBusy(true);
    try {
      await api.upsertPnl({
        fiscalYear: fy.trim(),
        income: income.map((r) => ({ id: r.id, name: r.name, amount: parseFloat(r.amount) || 0 })),
        expenses: expenses.map((r) => ({ id: r.id, name: r.name, amount: parseFloat(r.amount) || 0 })),
        taxes: taxes.map((r) => ({ id: r.id, name: r.name, amount: parseFloat(r.amount) || 0 })),
        notes,
      });
      toast({ title: "Saved", description: `${fy} — PAT ${fmtINR(totals.pat, { decimals: 2 })}` });
      await loadList();
    } catch (e) {
      toast({ title: "Save failed", description: e.response?.data?.detail || "Try again" });
    }
    setBusy(false);
  };

  const remove = async () => {
    const existing = statements.find((s) => s.fiscalYear === fy);
    if (!existing) return;
    if (!window.confirm(`Delete P&L for ${fy}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.deletePnl(fy);
      toast({ title: "Deleted", description: `${fy} removed` });
      await loadList();
      setFy(currentFY());
    } catch (e) {
      toast({ title: "Delete failed", description: e.response?.data?.detail || "Try again" });
    }
    setDeleting(false);
  };

  const existing = statements.find((s) => s.fiscalYear === fy);

  return (
    <div className="grid lg:grid-cols-5 gap-5" data-testid="pnl-admin-form">
      <div className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6">
        <div className="text-sm font-semibold text-white">Equity P&amp;L Statement</div>
        <div className="text-xs text-slate-500">
          Add / rename / delete account heads under Income, Expenses, and Taxes.
        </div>

        {/* FY selector */}
        <div className="mt-5">
          <Label className="text-slate-300 text-xs uppercase tracking-wider">Fiscal Year</Label>
          <div className="mt-1.5 flex gap-2 flex-wrap">
            <Input
              value={fy}
              onChange={(e) => setFy(e.target.value)}
              placeholder="FY 2025-2026"
              data-testid="pnl-fy-input"
              className="bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500 max-w-xs"
            />
            {statements.map((s) => (
              <button
                key={s.fiscalYear}
                onClick={() => setFy(s.fiscalYear)}
                className={`px-3 rounded-md text-xs font-medium border whitespace-nowrap ${
                  s.fiscalYear === fy
                    ? "bg-emerald-500 text-slate-950 border-emerald-500"
                    : "bg-slate-950 text-slate-400 border-slate-800 hover:text-white"
                }`}
              >
                {s.fiscalYear}
              </button>
            ))}
          </div>
        </div>

        <EquitySection
          title="Income from Equity"
          accent="emerald"
          rows={income}
          totalLabel="Total Income"
          total={totals.gross}
          onChangeRow={(id, field, v) => updateRow(setIncome, id, field, v)}
          onAdd={() => addRow(setIncome)}
          onRemove={(id) => removeRow(setIncome, id)}
          idPrefix="pnl-admin-income"
        />

        <EquitySection
          title="Expenses"
          accent="rose"
          rows={expenses}
          totalLabel="Total Expenses"
          total={totals.totalExp}
          onChangeRow={(id, field, v) => updateRow(setExpenses, id, field, v)}
          onAdd={() => addRow(setExpenses)}
          onRemove={(id) => removeRow(setExpenses, id)}
          idPrefix="pnl-admin-expense"
        />

        <EquitySection
          title="Taxes"
          accent="amber"
          rows={taxes}
          totalLabel="Total Tax"
          total={totals.totalTax}
          onChangeRow={(id, field, v) => updateRow(setTaxes, id, field, v)}
          onAdd={() => addRow(setTaxes)}
          onRemove={(id) => removeRow(setTaxes, id)}
          idPrefix="pnl-admin-tax"
        />

        {/* Notes */}
        <div className="mt-6">
          <Label className="text-slate-300 text-xs uppercase tracking-wider">Notes (optional)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any remarks for this FY…"
            rows={2}
            data-testid="pnl-admin-notes"
            className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500"
          />
        </div>

        <div className="mt-6 flex gap-3 flex-wrap">
          <Button
            onClick={save}
            disabled={busy}
            data-testid="pnl-admin-save"
            className="h-11 px-6 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold disabled:opacity-60"
          >
            <Save className="mr-2" size={16} />
            {busy ? "Saving…" : existing ? "Update statement" : "Save statement"}
          </Button>
          {existing && (
            <Button
              onClick={remove}
              disabled={deleting}
              data-testid="pnl-admin-delete"
              variant="ghost"
              className="h-11 px-4 border border-rose-500/40 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10"
            >
              <Trash2 className="mr-2" size={14} />
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          )}
        </div>
      </div>

      {/* Final figures */}
      <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6 h-fit sticky top-6">
        <div className="flex items-center gap-2 text-emerald-400">
          <Calculator size={16} />
          <div className="text-sm font-semibold">Final figures</div>
        </div>
        <dl className="mt-4 text-sm space-y-2">
          <KV k="Gross Income" v={fmtINR(totals.gross, { decimals: 2 })} positive />
          <KV k="Total Expenses" v={fmtINR(totals.totalExp, { decimals: 2 })} negative />
          <KV k="Total Tax" v={fmtINR(totals.totalTax, { decimals: 2 })} negative />
          <div className="h-px bg-slate-800 my-2" />
          <KV k="EBITA" v={fmtINR(totals.ebita, { decimals: 2 })} bold
             positive={totals.ebita >= 0} negative={totals.ebita < 0} />
          <KV k="PAT" v={fmtINR(totals.pat, { decimals: 2 })} bold
             positive={totals.pat >= 0} negative={totals.pat < 0} />
        </dl>
        <div className="mt-3 text-[11px] text-slate-500 space-y-1">
          <div>EBITA = Gross Income − Total Expenses</div>
          <div>PAT = EBITA − Total Tax</div>
        </div>
      </div>
    </div>
  );
}

function EquitySection({
  title, accent, rows, total, totalLabel,
  onChangeRow, onAdd, onRemove, idPrefix,
}) {
  const accentColor = {
    emerald: "text-emerald-400",
    rose: "text-rose-400",
    amber: "text-amber-400",
  }[accent];

  return (
    <div className="mt-6" data-testid={`${idPrefix}-section`}>
      <div className={`text-[11px] font-semibold uppercase tracking-widest ${accentColor} mb-3`}>
        {title}
      </div>
      <div className="rounded-lg border border-slate-800 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 bg-slate-950/40 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
          <div className="col-span-8">Account Head</div>
          <div className="col-span-3 text-right">Amount</div>
          <div className="col-span-1" />
        </div>
        {rows.length === 0 && (
          <div className="px-3 py-4 text-xs text-slate-500 italic text-center">
            No entries. Click "Add entry" below.
          </div>
        )}
        {rows.map((r) => (
          <div
            key={r.id}
            className="grid grid-cols-12 gap-2 items-center px-3 py-1.5 border-b border-slate-800/70 last:border-0"
            data-testid={`${idPrefix}-row-${r.id}`}
          >
            <Input
              value={r.name}
              onChange={(e) => onChangeRow(r.id, "name", e.target.value)}
              data-testid={`${idPrefix}-name-${r.id}`}
              className="col-span-8 h-9 bg-slate-950 border-slate-800 text-white text-xs focus-visible:ring-emerald-500"
            />
            <Input
              type="number"
              step="0.01"
              value={r.amount}
              onChange={(e) => onChangeRow(r.id, "amount", e.target.value)}
              data-testid={`${idPrefix}-amount-${r.id}`}
              className="col-span-3 h-9 bg-slate-950 border-slate-800 text-white text-right font-mono text-xs focus-visible:ring-emerald-500"
            />
            <button
              onClick={() => onRemove(r.id)}
              data-testid={`${idPrefix}-remove-${r.id}`}
              className="col-span-1 h-9 rounded-md hover:bg-rose-500/10 text-slate-500 hover:text-rose-400 flex items-center justify-center"
              title="Remove"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <div className="grid grid-cols-12 gap-2 bg-slate-950/60 px-3 py-2 font-semibold text-sm">
          <div className="col-span-8 text-slate-100">{totalLabel}</div>
          <div
            className={`col-span-3 text-right font-mono ${accentColor}`}
            data-testid={`${idPrefix}-total`}
          >
            {fmtINR(total, { decimals: 2 })}
          </div>
          <div className="col-span-1" />
        </div>
      </div>
      <button
        onClick={onAdd}
        data-testid={`${idPrefix}-add`}
        className="mt-2 inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white border border-dashed border-slate-700 hover:border-slate-500 rounded-md px-3 py-1.5"
      >
        <Plus size={12} /> Add entry
      </button>
    </div>
  );
}

function KV({ k, v, positive, negative, bold }) {
  const color = positive ? "text-emerald-400" : negative ? "text-rose-400" : "text-slate-200";
  return (
    <div className="flex items-center justify-between">
      <dt className="text-slate-400 text-xs">{k}</dt>
      <dd className={`text-sm font-mono ${bold ? "font-semibold text-white" : color}`}>{v}</dd>
    </div>
  );
}
